import { DISPLAY_INVESTOR_ACCOUNT_STATUS } from '@/utils/static';
import { dateString } from '@/utils/helpers/common';
import { useTranslation } from 'react-i18next';
import { Button, Popconfirm, Table, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { BREAKPOINTS, Flex } from '@/ui';
import { useDispatch, useSelector } from 'react-redux';
import { adminActions } from '@/store/admin';
import { superAdminApi } from '@/api';
import { usePopups } from '@/hooks';
import styled from 'styled-components';

const linkTitles = {
  questionnaireIds: 'Questionnaire',
  productIds: 'Product',
  requestDocumentIds: 'Document'
};
const linkUrls = {
  questionnaireIds: 'questionnaire',
  productIds: 'product',
  requestDocumentIds: 'documents'
};

export const InvestorsTable = ({
  data = [],
  investorStatusType,
  total,
  onChange,
  refetchInvestors
}) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = usePopups();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { investorsOnPage } = useSelector((state) => state.admin);
  const { userRole } = useSelector((state) => state.user);

  const deleteInvestor = async (id) => {
    try {
      const result = await superAdminApi.deleteInvestor({ investorId: +id });

      if (result) {
        showSuccessToast('Investor is deleted');
        refetchInvestors();
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e, true);
    }
  };

  const columns = [
    {
      title: t('Client name'),
      dataIndex: 'clientName',
      key: 'clientName',
      fixed: 'left',
      width: '160px'
    },
    {
      title: t('Tier'),
      dataIndex: 'tier',
      key: 'tier',
      width: '120px',
      responsive: ['lg']
    },
    {
      title: t('E-mail'),
      dataIndex: 'email',
      key: 'email',
      width: '150px',
      responsive: ['sm'],
      render: (text) => <Typography.Link href={`mailto:${text}`}>{text}</Typography.Link>
    },
    {
      title: t('Role'),
      key: 'role',
      dataIndex: 'role',
      responsive: ['md']
    },
    {
      title: t('Client status'),
      key: 'clientStatus',
      dataIndex: 'clientStatus',
      responsive: ['lg'],
      width: '120px'
    },
    {
      title: t('Created at'),
      key: 'createdAt',
      dataIndex: 'createdAt',
      responsive: ['lg']
    },
    {
      title: t('Updated at'),
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      responsive: ['lg']
    },
    {
      title: t('On review'),
      key: 'review',
      dataIndex: 'review',
      fixed: 'right',
      width: '80px',
      render: ({ investorId, reviewIds }) => {
        if (reviewIds) {
          const idsObject = Object.values(reviewIds).flat();
          return (
            <>
              {reviewIds && idsObject && idsObject.length > 0 ? (
                <Flex vertical align='flex-start'>
                  {Object.entries(reviewIds).map((idGroup) =>
                    idGroup[1].map((id) => {
                      return (
                        <Button
                          type='link'
                          size='small'
                          onClick={() =>
                            navigate(`/investor/${investorId}/${linkUrls[idGroup[0]]}/${id}`)
                          }
                        >
                          {t(linkTitles[idGroup[0]])}
                        </Button>
                      );
                    })
                  )}
                </Flex>
              ) : (
                <span>-</span>
              )}
            </>
          );
        } else <></>;
      },
      hidden: investorStatusType !== 0
    },
    {
      title: t('Link'),
      key: 'link',
      dataIndex: 'link',
      fixed: 'right',
      width: '80px',
      render: (text) => (
        <Button type='primary' size='small' onClick={() => navigate(`/investor/${text}`)}>
          {t('Details')}
        </Button>
      )
    },
    {
      title: '',
      key: 'delete',
      dataIndex: 'delete',
      fixed: 'right',
      width: '80px',
      render: (text) => (
        <Popconfirm
          title={t('Delete investor')}
          description={t('Are you sure you want to delete this investor?')}
          okText={t('Yes')}
          cancelText={t('No')}
          onConfirm={() => deleteInvestor(text)}
        >
          <Button type='primary' size='small' danger>
            {t('Delete')}
          </Button>
        </Popconfirm>
      ),
      hidden: userRole !== 'superAdmin'
    }
  ];

  const dataSource = data.map((item) => {
    return {
      key: item.id,
      clientName: `${item.firstName} ${item.lastName}`,
      tier: item.tier ? `${t('Tier')} ${item.tier}` : '-',
      email: item.email,
      role: item.isAgent ? 'Agent' : item.isRecommender ? 'Assistant' : 'Investor',
      clientStatus: t(DISPLAY_INVESTOR_ACCOUNT_STATUS[item.accountStatus]),
      createdAt: dateString(new Date(item.createdAt)),
      updatedAt: dateString(new Date(item.updatedAt)),
      review: { investorId: item.id, reviewIds: item.reviewIds },
      link: item.id,
      delete: item.id
    };
  });

  return (
    <StyledTable
      bordered
      dataSource={dataSource}
      columns={columns}
      scroll={{ x: 'max-content' }}
      pagination={{
        defaultPageSize: investorsOnPage,
        total: total,
        onChange: (page) => onChange(page - 1),
        position: ['bottomCenter'],
        onShowSizeChange: (current, size) => dispatch(adminActions.setInvestorsOnPage(size)),
        pageSizeOptions: [10, 20, 40, 50, 100]
      }}
      size={window.innerWidth <= BREAKPOINTS.sm ? 'small' : 'large'}
    />
  );
};

const StyledTable = styled(Table)`
  .ant-table-cell {
    padding: 18px 12px !important;
  }
`;
